import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useContactForm } from "@/contexts/ContactFormContext";
import { useTranslation } from "@/i18n/LanguageContext";
import LanguageToggle from "./LanguageToggle";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeSection, setActiveSection] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const { openForm } = useContactForm();
  const { t } = useTranslation();

  const navLinks = [
    { label: t("nav.work"), target: "showcase" },
    { label: t("nav.process"), target: "process" },
    { label: t("nav.pricing"), target: "offers" },
  ];

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setScrolled(window.scrollY > 50);
        const h = document.documentElement.scrollHeight - window.innerHeight;
        setProgress(h > 0 ? (window.scrollY / h) * 100 : 0);
        ticking = false;
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-40% 0px -40% 0px" }
    );

    const ids = ["showcase", "process", "offers", "cta"];
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => { window.removeEventListener("scroll", onScroll); observer.disconnect(); };
  }, []);

  const scrollTo = (id: string) => {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <div className="fixed top-0 left-0 w-full h-[2px] z-[60]">
        <div className="h-full bg-primary/60 transition-[width] duration-100" style={{ width: `${progress}%` }} />
      </div>

      <motion.nav
        className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-10 py-4 transition-all duration-500 ${
          scrolled
            ? "bg-background/80 backdrop-blur-md border-b border-border/10 shadow-[0_4px_20px_rgba(0,0,0,0.3)]"
            : "bg-transparent"
        }`}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          className="group flex flex-col items-start transition-colors"
        >
          <span className="font-display text-lg font-bold text-foreground tracking-tight group-hover:text-primary group-hover:drop-shadow-[0_0_8px_hsl(191_100%_50%/0.6)] transition-all duration-300">ZEPHR<span className="text-primary">.</span></span>
          <span className="font-display text-[7px] tracking-[0.35em] text-muted-foreground/60 uppercase -mt-1 group-hover:text-primary/60 transition-colors duration-300">STUDIO</span>
        </a>

        <div className="flex items-center gap-4 md:gap-6">
          {/* Desktop nav links */}
          {navLinks.map((link) => (
            <button
              key={link.target}
              onClick={() => scrollTo(link.target)}
              className={`relative hidden md:block text-sm transition-colors duration-300 ${
                scrolled ? "opacity-100" : "opacity-0 pointer-events-none"
              } ${activeSection === link.target ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
            >
              {link.label}
              {activeSection === link.target && (
                <motion.div
                  className="absolute -bottom-1 left-0 right-0 h-px bg-primary"
                  layoutId="nav-indicator"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
            </button>
          ))}

          {/* Language toggle — desktop */}
          <div className={`hidden md:block transition-opacity duration-300 ${scrolled ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
            <LanguageToggle />
          </div>

          {/* Desktop CTA */}
          <button
            onClick={() => { setMobileOpen(false); openForm(); }}
            className={`hidden md:block text-sm font-medium px-4 py-2 rounded-full border transition-all duration-300 ${
              scrolled
                ? "border-primary/30 text-primary hover:bg-primary/10"
                : "border-foreground/20 text-foreground hover:border-primary/40 hover:text-primary"
            }`}
          >
            {t("nav.bookCall")}
          </button>

          {/* Mobile hamburger */}
          <button
            className="md:hidden w-9 h-9 flex items-center justify-center text-foreground"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </motion.nav>

      {/* Mobile overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="fixed inset-0 z-[70] bg-background flex flex-col items-center justify-center gap-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <button
              className="absolute top-5 right-6 w-9 h-9 flex items-center justify-center text-foreground"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Language toggle — mobile, above nav links */}
            <LanguageToggle className="text-sm" />

            {navLinks.map((link, i) => (
              <motion.button
                key={link.target}
                onClick={() => scrollTo(link.target)}
                className={`font-display text-2xl font-bold transition-colors ${
                  activeSection === link.target ? "text-primary" : "text-foreground"
                }`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 + i * 0.08 }}
              >
                {link.label}
              </motion.button>
            ))}

            <motion.button
              onClick={() => { setMobileOpen(false); openForm(); }}
              className="mt-4 px-6 py-3 rounded-full border border-primary/40 text-primary font-medium text-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              {t("nav.bookCall")}
            </motion.button>

            <motion.p
              className="text-xs text-muted-foreground/40 tracking-widest uppercase mt-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              Brand & Web Design Studio
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
